import React, { useEffect, useState } from 'react';
import { HiArrowLeft, HiArrowRight, HiTrash } from 'react-icons/hi2';
import {
  getDeviceTypes,
  uploadDeviceImage,
  updateDeviceMetadata,
  updateDeviceType
} from '../utils/api';

export default function MetadataStep({ onBack, onNext, ocrData }) {
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(ocrData?.deviceType || '');
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [customMetadata, setCustomMetadata] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const parseValue = (val) => {
    if (val === 'true') return true;
    if (val === 'false') return false;
    if (!isNaN(val) && val.trim() !== '') return Number(val);
    return val;
  };

  useEffect(() => {
    getDeviceTypes()
      .then(setDeviceTypes)
      .catch(() => setDeviceTypes([]));
  }, []);

  useEffect(() => {
    if (selectedType) {
      const selected = deviceTypes.find((d) => d.id === selectedType);
      if (selected) {
        setFields(selected.fields || []);
        setFormData({});
      }
    }
  }, [selectedType, deviceTypes]);

  useEffect(() => {
    if (ocrData?.metadata?.length) {
      setCustomMetadata(ocrData.metadata);
    } else {
      setCustomMetadata([{ label: '', value: '' }]);
    }
  }, [ocrData]);

  useEffect(() => {
    if (ocrData?.deviceType) {
      setSelectedType(ocrData.deviceType);
    }
  }, [ocrData?.deviceType]);

  useEffect(() => {
    if (error) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [error]);

  const handleTypeChange = async (e) => {
    const newType = e.target.value;
    setSelectedType(newType);
    try {
      const res = await updateDeviceType(ocrData.eui, newType);
      if (res.status !== 204) {
        setError('Failed to update device type.');
      }
    } catch (err) {
      console.error('Device type update failed', err);
      setError(
        err.response?.data?.message ||
        'Could not update device type. Please check permissions.'
      );
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMetadataChange = (index, key, newValue) => {
    const updated = [...customMetadata];
    updated[index][key] = newValue;
    setCustomMetadata(updated);
  };

  const addMetadataRow = () => setCustomMetadata([...customMetadata, { label: '', value: '' }]);
  const removeMetadataRow = (index) => setCustomMetadata(customMetadata.filter((_, i) => i !== index));

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const uploaded = [];
    setUploading(true);
    for (const file of files) {
      try {
        const imageUrl = await uploadDeviceImage(file);
        uploaded.push(imageUrl);
      } catch (err) {
        console.error('Failed to upload image:', file.name);
      }
    }
    setImages((prev) => [...prev, ...uploaded]);
    setUploading(false);
  };

  const removeImage = (index) => setImages(images.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (!selectedType) {
      setError('Please select a device type.');
      return;
    }
    const missing = fields.some((field) => !formData[field.name]?.trim());
    if (missing) {
      setError('Please fill in all required fields.');
      return;
    }

    const mergedMetadata = {
      ...Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [key, parseValue(value)])
      ),
      ...Object.fromEntries(
        customMetadata
          .filter((m) => m.label && m.value)
          .map(({ label, value }) => [label, parseValue(value)])
      )
    };

    try {
      const metaRes = await updateDeviceMetadata(ocrData.eui, mergedMetadata);

      if (metaRes.status === 204 || metaRes?.status === 200) {
        onNext({ ...ocrData, metadata: mergedMetadata, deviceType: selectedType });
      } else {
        setError('Failed to save metadata.');
      }
    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 403) {
        setError('You do not have permission to perform this action.');
      } else {
        setError('Error submitting metadata.');
      }
    }
  };

  const selectedDevice = deviceTypes.find((d) => d.id === selectedType);

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="space-y-8">
        <h1 className="text-2xl font-semibold text-gray-800">Device Metadata</h1>

        <div>
          <label className="block text-sm mb-1 text-gray-600">Device Type</label>
          <select
            value={selectedType}
            onChange={handleTypeChange}
            className="w-full border px-4 py-2 rounded-lg"
            disabled={!!ocrData?.deviceType} // disable if pre-selected from OCR
          >
            <option value="">Select a device type</option>
            {deviceTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>

          {selectedDevice?.guide && (
            <div className="mt-3 flex items-center gap-4">
              <img
                src={selectedDevice.guide.image}
                alt="Guide"
                className="w-16 h-16 object-contain border rounded"
              />
              <a
                href={selectedDevice.guide.pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline text-sm"
              >
                View Installation Guide (PDF)
              </a>
            </div>
          )}
        </div>

        {fields.length > 0 && (
          <div className="space-y-4">
            {fields.map((field, i) => (
              <div key={i}>
                <label className="block text-sm text-gray-600 mb-1">{field.label}</label>
                <input
                  type="text"
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                  className="w-full border px-4 py-2 rounded-lg"
                  placeholder={field.placeholder || ''}
                />
              </div>
            ))}
          </div>
        )}

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Metadata</h4>
          <div className="space-y-3">
            {customMetadata.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Label"
                  className="w-1/3 px-2 py-1 border rounded-md text-sm"
                  value={item.label}
                  onChange={(e) => handleMetadataChange(index, 'label', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Value"
                  className="flex-1 px-2 py-1 border rounded-md text-sm"
                  value={item.value}
                  onChange={(e) => handleMetadataChange(index, 'value', e.target.value)}
                />
                {customMetadata.length > 1 && (
                  <button
                    onClick={() => removeMetadataRow(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addMetadataRow}
              className="text-primary text-sm mt-2 hover:underline"
            >
              + Add Field
            </button>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Device Photos</h4>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="w-full border rounded-md px-4 py-2 text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-white hover:file:bg-primary/90"
          />
          {uploading && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
          <div className="flex flex-wrap gap-3 mt-2">
            {images.map((url, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={url}
                  alt={`Uploaded ${idx}`}
                  className="w-20 h-20 object-cover border rounded"
                />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 p-1 bg-white rounded-full shadow hover:bg-gray-100 hidden group-hover:block"
                >
                  <HiTrash className="w-4 h-4 text-red-600" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>

      <div className="pt-6 flex justify-between mt-10">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gray-200 text-sm text-gray-800 hover:bg-gray-300 transition"
        >
          <HiArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleSubmit}
          className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition"
        >
          Submit
          <HiArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
